/**
 * Platform operations console contracts (ops/admin/platform roles).
 */
import { GHALISHOR_API_V1_PREFIX } from "./identity-access";
import type {
  BusinessStatus,
  IsoDateTimeString,
  UuidString,
} from "./identity-access";
import type { HealthResponse } from "./api";
import type { ServiceRequestStatus } from "./customer-requests";

export const PLATFORM_OPS_ROLES = Object.freeze([
  "support",
  "finance",
  "ops",
  "admin",
  "platform",
] as const);
export type PlatformOpsRole = (typeof PLATFORM_OPS_ROLES)[number];

export const PLATFORM_ROLE_VALUES = Object.freeze([
  "ops",
  "admin",
  "platform",
  "support",
  "finance",
  "customer",
  "business_member",
] as const);
export type PlatformRoleValue = (typeof PLATFORM_ROLE_VALUES)[number];
export const PLATFORM_ASSIGNABLE_ROLES = Object.freeze([
  "support",
  "finance",
  "ops",
  "admin",
] as const);
export type PlatformAssignableRole = (typeof PLATFORM_ASSIGNABLE_ROLES)[number];

export const PLATFORM_ACCOUNT_ROLES = Object.freeze([
  "platform_owner",
  "platform_admin",
  "platform_ops",
  "platform_support",
  "platform_finance",
  "business_owner",
  "business_member",
  "customer",
] as const);
export type PlatformAccountRole = (typeof PLATFORM_ACCOUNT_ROLES)[number];

export function isPlatformOpsRole(
  value: string | null | undefined,
): value is PlatformOpsRole {
  return (
    value === "support" ||
    value === "finance" ||
    value === "ops" ||
    value === "admin" ||
    value === "platform"
  );
}

export function isPlatformRoleValue(
  value: string | null | undefined,
): value is PlatformRoleValue {
  return (
    value === "ops" ||
    value === "admin" ||
    value === "platform" ||
    value === "support" ||
    value === "finance" ||
    value === "customer" ||
    value === "business_member"
  );
}

export const PLATFORM_OPS_ROUTES = Object.freeze({
  me: `${GHALISHOR_API_V1_PREFIX}/platform/me`,
  dashboard: `${GHALISHOR_API_V1_PREFIX}/platform/dashboard`,
  businesses: `${GHALISHOR_API_V1_PREFIX}/platform/businesses`,
  business: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/businesses/${encodeURIComponent(id)}`,
  users: `${GHALISHOR_API_V1_PREFIX}/platform/users`,
  user: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/users/${encodeURIComponent(id)}`,
  customers: `${GHALISHOR_API_V1_PREFIX}/platform/directory/customers`,
  customer: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/directory/customers/${encodeURIComponent(id)}`,
  businessMembers: `${GHALISHOR_API_V1_PREFIX}/platform/directory/business-members`,
  businessMember: (membershipId: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/directory/business-members/${encodeURIComponent(membershipId)}`,
  platformTeam: `${GHALISHOR_API_V1_PREFIX}/platform/directory/platform-team`,
  platformTeamMember: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/directory/platform-team/${encodeURIComponent(id)}`,
  accountSecurity: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/accounts/${encodeURIComponent(id)}/security`,
  resetAccountPassword: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/accounts/${encodeURIComponent(id)}/reset-password`,
  revokeAccountSessions: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/accounts/${encodeURIComponent(id)}/revoke-sessions`,
  unlockAccount: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/accounts/${encodeURIComponent(id)}/unlock`,
  fleetDrivers: `${GHALISHOR_API_V1_PREFIX}/platform/fleet/drivers`,
  requests: `${GHALISHOR_API_V1_PREFIX}/platform/requests`,
  request: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/requests/${encodeURIComponent(id)}`,
  rematchRequest: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/requests/${encodeURIComponent(id)}/rematch`,
  noteRequest: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/requests/${encodeURIComponent(id)}/note`,
  retryRequestNotification: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/requests/${encodeURIComponent(id)}/retry-notification`,
  audit: `${GHALISHOR_API_V1_PREFIX}/platform/audit`,
} as const);

export interface PlatformMeResponse {
  readonly userId: UuidString;
  readonly mobileE164: string;
  readonly platformRole: PlatformOpsRole;
  readonly allowed: true;
  /** Phase 2: block console UI until enough Passkeys are enrolled. */
  readonly passkeyEnrollmentRequired: boolean;
  readonly passkeyCount: number;
  readonly passkeysNeeded: number;
  readonly minimumPasskeys: number;
  readonly capabilities: readonly string[];
}

export interface PlatformRematchResponse {
  readonly requestId: UuidString;
  readonly rematched: boolean;
  readonly status: string;
}

export interface PlatformRequestNoteRequest {
  readonly note: string;
}

export interface PlatformAuditEntry {
  readonly id: UuidString;
  readonly action: string;
  readonly targetType: string;
  readonly targetId: string | null;
  readonly actorUserId: string | null;
  readonly metadata: Record<string, unknown>;
  readonly createdAt: IsoDateTimeString;
}

export interface ListPlatformAuditResponse {
  readonly items: readonly PlatformAuditEntry[];
}

export interface PlatformDashboardResponse {
  readonly health: HealthResponse;
  readonly openRequestCount: number;
  readonly activeBusinessCount: number;
  readonly newPartnershipInquiryCount: number;
  readonly userCount: number;
  readonly attentionRequestCount: number;
}

export interface PlatformBusinessSummary {
  readonly id: UuidString;
  readonly slug: string;
  readonly name: string;
  readonly status: BusinessStatus;
  readonly ownerUserId: UuidString;
  readonly ownerMobileE164: string | null;
  readonly createdAt: IsoDateTimeString;
  readonly updatedAt: IsoDateTimeString;
}

export interface ListPlatformBusinessesResponse {
  readonly items: readonly PlatformBusinessSummary[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

export interface PlatformBusinessDetail extends PlatformBusinessSummary {
  readonly city: string | null;
  readonly addressText: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly serviceRadiusKm: number | null;
  readonly memberCount: number;
  readonly driverCount: number;
  readonly activeRequestCount: number;
  readonly members: readonly PlatformBusinessMemberDetail[];
}

export interface UpdatePlatformBusinessRequest {
  readonly status: BusinessStatus;
}

export interface PlatformUserSummary {
  readonly id: UuidString;
  readonly mobileE164: string;
  readonly displayName: string | null;
  readonly fullName: string | null;
  readonly isActive: boolean;
  readonly platformRole: string | null;
  /** Stable role derived from the account's origin and business ownership. */
  readonly accountRole: PlatformAccountRole;
  readonly roleSource: "platform" | "business" | "registration";
  readonly businessNames: readonly string[];
  readonly businessRoleKeys: readonly string[];
  readonly createdAt: IsoDateTimeString;
}

export interface ListPlatformUsersResponse {
  readonly items: readonly PlatformUserSummary[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

export interface PlatformCustomerSummary {
  readonly id: UuidString;
  readonly mobileE164: string;
  readonly fullName: string | null;
  readonly displayName: string | null;
  readonly city: string | null;
  readonly addressText: string | null;
  readonly addressLat: number | null;
  readonly addressLng: number | null;
  readonly isActive: boolean;
  readonly orderCount: number;
  readonly latestOrderAt: IsoDateTimeString | null;
  readonly latestServiceLabels: readonly string[];
  readonly createdAt: IsoDateTimeString;
}

export interface PlatformCustomerOrderSummary {
  readonly id: UuidString;
  readonly status: string;
  readonly carpetType: string;
  readonly carpetCount: number;
  readonly approximateSqm: number;
  readonly services: readonly string[];
  readonly businessName: string | null;
  readonly createdAt: IsoDateTimeString;
  readonly updatedAt: IsoDateTimeString;
}

export interface PlatformCustomerDetail extends PlatformCustomerSummary {
  readonly profileUpdatedAt: IsoDateTimeString | null;
  readonly orders: readonly PlatformCustomerOrderSummary[];
}

export interface ListPlatformCustomersResponse {
  readonly items: readonly PlatformCustomerSummary[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

export interface PlatformBusinessMemberSummary {
  readonly membershipId: UuidString;
  readonly userId: UuidString;
  readonly businessId: UuidString;
  readonly businessName: string;
  readonly mobileE164: string;
  readonly fullName: string | null;
  readonly displayName: string | null;
  readonly status: string;
  readonly isPrimaryOwner: boolean;
  readonly roleKey: string;
  readonly roleLabel: string;
  readonly createdAt: IsoDateTimeString;
  readonly updatedAt: IsoDateTimeString;
}

export interface PlatformBusinessMemberDetail extends PlatformBusinessMemberSummary {
  readonly capabilities: readonly string[];
  readonly addressText: string | null;
  readonly city: string | null;
  readonly lastActivityAt: IsoDateTimeString | null;
}

export interface ListPlatformBusinessMembersResponse {
  readonly items: readonly PlatformBusinessMemberSummary[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

export interface UpdatePlatformBusinessMemberRequest {
  readonly roleKey?:
    "operations_manager" | "finance_manager" | "workshop_manager" | "driver";
  readonly status?: "active" | "inactive";
}

export interface PlatformTeamMemberSummary {
  readonly id: UuidString;
  readonly mobileE164: string;
  readonly fullName: string | null;
  readonly displayName: string | null;
  readonly role: PlatformOpsRole;
  readonly capabilities: readonly string[];
  readonly isActive: boolean;
  readonly isOwner: boolean;
  readonly createdAt: IsoDateTimeString;
  readonly updatedAt: IsoDateTimeString;
}

export interface ListPlatformTeamResponse {
  readonly items: readonly PlatformTeamMemberSummary[];
  readonly total: number;
}

export interface CreatePlatformTeamMemberRequest {
  readonly mobile: string;
  readonly fullName: string;
  readonly role: PlatformAssignableRole;
  readonly temporaryPassword: string;
}

export interface UpdatePlatformTeamMemberRequest {
  readonly role?: PlatformAssignableRole;
  readonly isActive?: boolean;
}

export interface PlatformAccountSecurityStatus {
  readonly userId: UuidString;
  readonly mobileE164: string;
  readonly displayName: string | null;
  readonly isActive: boolean;
  readonly platformRole: PlatformOpsRole | null;
  readonly hasPassword: boolean;
  readonly mustChangePassword: boolean;
  readonly failedAttemptCount: number;
  readonly lockedUntil: IsoDateTimeString | null;
  readonly activeSessionCount: number;
  readonly passwordUpdatedAt: IsoDateTimeString | null;
}

export interface PlatformAccountSupportRequest {
  /** Required operational reason, recorded in the audit log. */
  readonly reason: string;
}

export interface PlatformResetAccountPasswordRequest extends PlatformAccountSupportRequest {
  readonly temporaryPassword: string;
}

export interface PlatformAccountSupportActionResponse {
  readonly action: "password_reset" | "sessions_revoked" | "account_unlocked";
  readonly revokedSessionCount: number;
  readonly status: PlatformAccountSecurityStatus;
}

export interface PlatformFleetDriverSummary {
  readonly membershipId: UuidString;
  readonly userId: UuidString;
  readonly businessId: UuidString;
  readonly businessName: string;
  readonly driverName: string | null;
  readonly mobileE164: string;
  readonly status: "offline" | "available" | "on_route" | "paused";
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly accuracyMeters: number | null;
  readonly speedMetersPerSecond: number | null;
  readonly headingDegrees: number | null;
  readonly recordedAt: IsoDateTimeString | null;
  readonly isStale: boolean;
  readonly activeRunCount: number;
}

export interface ListPlatformFleetDriversResponse {
  readonly items: readonly PlatformFleetDriverSummary[];
  readonly generatedAt: IsoDateTimeString;
  readonly mapProvider: "internal" | "neshan";
}

export interface UpdatePlatformUserRequest {
  /** Pass null to clear platform_role. */
  readonly platformRole?: PlatformRoleValue | null;
  /** Platform account managers may block or restore an account. */
  readonly isActive?: boolean;
}

export interface DeletePlatformEntityResponse {
  readonly id: UuidString;
  readonly deleted: true;
}

export interface PlatformRequestSummary {
  readonly id: UuidString;
  readonly status: ServiceRequestStatus | string;
  readonly customerUserId: UuidString;
  readonly customerMobileE164: string | null;
  readonly carpetType: string;
  readonly carpetCount: number;
  readonly approximateSqm: number;
  readonly acceptedBusinessId: string | null;
  readonly acceptedBusinessName: string | null;
  readonly confirmedPrice: number | null;
  readonly priceNote: string | null;
  readonly scheduledPickupAt: IsoDateTimeString | null;
  readonly scheduledReturnAt: IsoDateTimeString | null;
  readonly scheduleState: string | null;
  readonly waitingMinutes: number;
  readonly attentionRequired: boolean;
  readonly attentionReason: string | null;
  readonly submittedAt: IsoDateTimeString | null;
  readonly createdAt: IsoDateTimeString;
  readonly updatedAt: IsoDateTimeString;
}

export interface ListPlatformRequestsResponse {
  readonly items: readonly PlatformRequestSummary[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

export interface PlatformRequestDetail extends PlatformRequestSummary {
  readonly notes: string | null;
  readonly services: readonly string[];
  readonly preferredBusinessId: string | null;
  readonly maxReturnWaitDays: number | null;
  readonly timeline: readonly PlatformWorkflowEvent[];
}

export interface PlatformWorkflowEvent {
  readonly id: UuidString;
  readonly action: string;
  readonly actorUserId: string | null;
  readonly metadata: Record<string, unknown>;
  readonly createdAt: IsoDateTimeString;
}

export interface PlatformRetryNotificationResponse {
  readonly requestId: UuidString;
  readonly configured: boolean;
  readonly attempted: number;
  readonly sent: number;
}
